import { useEffect, useState } from 'preact/hooks';
import { type Session, describeError } from '../api/client';
import { gratitudeMonth, putGratitude } from '../api/collections';
import { monthFrom, monthLabel, monthOf, shiftMonth, type MonthRange } from '../api/month';
import { dayLabel, dayNumber } from '../api/week';
import type { WireGratitudeEntry } from '../api/wire';

import { Pressable } from '../components/a11y/Pressable';

/** The month pattern: one month per page, days down the side, one entry per day.
 *
 *  A gratitude entry is a whole-day upsert rather than a revision-guarded
 *  resource — the day is the key and there is only ever one entry on it — so
 *  there is no `If-Match` here and no conflict to resolve.
 */
export function Gratitude({ session, journalDate }: { session: Session; journalDate: string }) {
  const [range, setRange] = useState<MonthRange>(() => monthOf(journalDate));
  const [entries, setEntries] = useState<WireGratitudeEntry[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function load(target: MonthRange) {
    try {
      setEntries(await gratitudeMonth(session, target.month));
      setError(null);
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  useEffect(() => {
    void load(range);
  }, [session, range]);

  const byDate = new Map((entries ?? []).map((e) => [e.journal_date, e]));

  async function save(date: string) {
    const text = draft.trim();
    if (!text) return;
    try {
      await putGratitude(session, date, text);
      setEditing(null);
      setDraft('');
      await load(range);
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  const written = entries?.length ?? 0;

  return (
    <main style={{ padding: 'var(--bujo-gutter)', maxWidth: 560, margin: '0 auto' }}>
      <header style={header}>
        <div style={eyebrow}>BUJO / COLLECTIONS</div>
        <h1 style={h1}>GRATITUDE · {monthLabel(range.month).slice(0, 3)} {range.month.slice(0, 4)}</h1>
        {/* The count is the fact worth leading with, and month stepping is an
            adjustment to it — so they share a line rather than the nav taking
            one of its own. */}
        <div style={subtitle}>
          <span>
            {written} OF {range.days.length} DAYS WRITTEN
          </span>
          <span style={{ color: 'var(--bujo-text-faint)' }}>·</span>
          <nav aria-label="Change month" style={{ display: 'flex', gap: 'var(--bujo-space-3)' }}>
            <button type="button" onClick={() => setRange(monthFrom(shiftMonth(range.month, -1)))} style={nav}>
              ◀ {monthLabel(shiftMonth(range.month, -1)).slice(0, 3)}
            </button>
            <button type="button" onClick={() => setRange(monthFrom(shiftMonth(range.month, 1)))} style={nav}>
              {monthLabel(shiftMonth(range.month, 1)).slice(0, 3)} ▶
            </button>
          </nav>
        </div>
      </header>

      {error ? <p style={errorLine}>! {error}</p> : null}

      {range.days.map((date) => {
        const entry = byDate.get(date);
        const isEditing = editing === date;
        const isToday = date === journalDate;
        return (
          <div key={date} style={{ ...row, background: isToday ? 'var(--bujo-select-ground)' : 'transparent' }}>
            <span style={{ ...dateCell, color: isToday ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)' }}>
              {dayNumber(date)} {dayLabel(date)}
            </span>
            {isEditing ? (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void save(date);
                }}
                style={{ flex: 1, display: 'flex', gap: 'var(--bujo-space-2)' }}
              >
                <input
                  value={draft}
                  onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
                  placeholder="one thing"
                  autocapitalize="sentences"
                  spellcheck
                  enterkeyhint="done"
                  autofocus
                  style={input}
                />
                <button type="submit" disabled={!draft.trim()} style={saveButton}>
                  SAVE
                </button>
              </form>
            ) : (
              <Pressable
                label={`${dayLabel(date)} ${dayNumber(date)}: ${entry?.content ?? 'not written'} — tap to write`}
                onPress={() => {
                  setEditing(date);
                  setDraft(entry?.content ?? '');
                }}
                style={{ flex: 1, minWidth: 0 }}
              >
                <span
                  style={{
                    display: 'block',
                    font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/var(--bujo-lh-row) var(--bujo-font-sans)',
                    color: entry ? 'var(--bujo-text-primary)' : 'var(--bujo-text-faint)',
                  }}
                >
                  {entry?.content ?? '— not yet written'}
                </span>
              </Pressable>
            )}
          </div>
        );
      })}
    </main>
  );
}

const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const subtitle = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 'var(--bujo-space-3)',
  flexWrap: 'wrap' as const,
  paddingTop: 'var(--bujo-space-1)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-text-meta)',
};

const row = {
  display: 'flex',
  gap: 'var(--bujo-space-3)',
  alignItems: 'center',
  minHeight: 'var(--bujo-row-min)',
  padding: '4px 0',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const dateCell = {
  width: 62,
  flex: 'none' as const,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.35 var(--bujo-font-mono)',
};
const input = {
  flex: 1,
  minWidth: 0,
  minHeight: 'var(--bujo-row-min)',
  background: 'var(--bujo-surface-sheet)',
  border: '1px solid var(--bujo-rule-hairline)',
  color: 'var(--bujo-text-primary)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-body)/1 var(--bujo-font-sans)',
  padding: '0 var(--bujo-space-2)',
  borderRadius: 0,
};
const saveButton = {
  flex: 'none' as const,
  minHeight: 'var(--bujo-row-min)',
  padding: '0 var(--bujo-space-3)',
  border: '1px solid var(--bujo-accent)',
  color: 'var(--bujo-accent)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-badge)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  cursor: 'pointer',
};
const nav = {
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  cursor: 'pointer',
};
const errorLine = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)',
  color: 'var(--bujo-state-error)',
};
